import { useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import {
  storyHistorySchema,
  storyLabels,
  type StoryField,
  type StoryHistoryEntry
} from "@eaf/contracts";
import { useAuth } from "../auth/auth-context";

export function StoryHistory({
  artworkId,
  field,
  disabled,
  onUse
}: {
  artworkId: string;
  field: StoryField;
  disabled: boolean;
  onUse: (text: string) => void | Promise<void>;
}) {
  const auth = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [entries, setEntries] = useState<StoryHistoryEntry[]>([]);
  const [selected, setSelected] = useState("");
  const show = async () => {
    setOpen(true);
    setLoading(true);
    setError("");
    setSelected("");
    setEntries([]);
    try {
      const result = storyHistorySchema.parse(
        await auth.authorizedFetch(`/curator/artworks/${artworkId}/storytelling/history`)
      );
      setEntries(result.history[field]);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Previous versions are unavailable.");
    } finally {
      setLoading(false);
    }
  };
  const useSelected = async () => {
    const entry = entries.find((item) => item.id === selected);
    if (!entry) return;
    setLoading(true);
    setError("");
    try {
      await onUse(entry.text);
      setOpen(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "This version could not be restored.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <Button
        variant="outline-light"
        disabled={disabled}
        onClick={() => void show()}
        aria-label={`Previous Versions for ${storyLabels[field]}`}
      >
        Previous Versions
      </Button>
      <Modal
        show={open}
        onHide={() => !loading && setOpen(false)}
        size="lg"
        centered
        scrollable
        aria-labelledby={`story-history-heading-${field}`}
      >
        <Modal.Header closeButton={!loading}>
          <Modal.Title id={`story-history-heading-${field}`}>
            Previous Versions — {storyLabels[field]}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {loading && <p role="status">Loading…</p>}
          {!loading && !error && !entries.length && <p>No previous versions yet.</p>}
          {entries.map((entry, index) => (
            <div key={entry.id} className="mb-4">
              <Form.Check
                type="radio"
                name={`history-${field}`}
                id={`history-${entry.id}`}
                label={`${index === 0 ? "Most recent" : "Earlier"} · ${new Date(entry.createdAt).toLocaleString()}`}
                checked={selected === entry.id}
                disabled={loading}
                onChange={() => setSelected(entry.id)}
              />
              <p className="mt-2" style={{ whiteSpace: "pre-wrap" }}>
                {entry.text}
              </p>
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" disabled={loading} onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button disabled={loading || !selected} onClick={() => void useSelected()}>
            Use This Version
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
